import { useEffect, useMemo } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppDispatch } from 'store';
import { changeTemplateValue } from 'store/slices/template';
// Import Enums
import { TemplateValueTypeEnum } from 'enums';
// Import Hooks
import useValidator from './UseValidator';
import useHasPermission from './UseHasPermission';
// Import Constants
import { TemplateInputTypes } from 'constants/common';
// Import Services
import { getTemplateValidationTranslatableErrorText } from 'services/template';

const useImageURLPlaceholder = ({ templateData, templateLangKey, templateType, dynamicVariables }) => {
    const { t } = useTranslation();
    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));
    const dispatch = useAppDispatch();
    const { hasSubscriptionPlaceholder } = useHasPermission();

    const handleImageURLPlaceholderDropdownChange = (data) => {
        if (!isEmpty(templateData?.FileName)) {
            dispatch(
                changeTemplateValue({
                    propertyName: TemplateInputTypes.FileName,
                    value: '',
                    templateLangKey,
                }),
            );
        }

        if (isNil(data)) {
            dispatch(
                changeTemplateValue({
                    propertyName: TemplateInputTypes.UploadedImageUrl,
                    value: '',
                    templateLangKey,
                }),
            );
        } else {
            dispatch(
                changeTemplateValue({
                    propertyName: TemplateInputTypes.UploadedImageUrl,
                    value: data.value,
                    templateLangKey,
                }),
            );
        }
    };

    const imageURLPlaceholdersData = useMemo(() => {
        return dynamicVariables
            .filter((item) => item.channels[templateType].TypeConfig?.ValueType === TemplateValueTypeEnum.Image)
            .map((item) => ({
                value: `{${item.channels[templateType].FieldName}}`,
                label: t(item.channels[templateType].DisplayNameKey),
                id: item.channels[templateType].Id,
            }));
    }, [dynamicVariables, templateType, t]);

    const allowedImageURLPlaceholdersData = useMemo(() => {
        return imageURLPlaceholdersData.filter((item) => hasSubscriptionPlaceholder(item.id));
    }, [imageURLPlaceholdersData, hasSubscriptionPlaceholder]);

    const selectedImageURLPlaceholder = useMemo(() => {
        return dynamicVariables.find(
            (item) => item.channels?.[templateType]?.FieldName === templateData?.UploadedImageUrl?.slice(1, -1),
        )?.channels?.[templateType];
    }, [dynamicVariables, templateData?.UploadedImageUrl, templateType]);

    const imagePreviewURL = isNil(selectedImageURLPlaceholder)
        ? templateData?.UploadedImageUrl
        : selectedImageURLPlaceholder?.PreviewValue;

    const imageURLPlaceholderDropdownData = isNil(selectedImageURLPlaceholder)
        ? allowedImageURLPlaceholdersData
        : imageURLPlaceholdersData;

    const isBlockedImageURLPlaceholderDropdown =
        isNil(selectedImageURLPlaceholder) &&
        !isEmpty(imageURLPlaceholdersData) &&
        isEmpty(allowedImageURLPlaceholdersData);

    const isDisabledUploadFileBtn = !isEmpty(templateData?.UploadedImageUrl) && !isNil(selectedImageURLPlaceholder);

    const isDisabledImageURLPlaceholderDropdown =
        !isBlockedImageURLPlaceholderDropdown &&
        !isEmpty(templateData?.UploadedImageUrl) &&
        isNil(selectedImageURLPlaceholder);

    useEffect(() => {
        validator({
            [templateLangKey + TemplateInputTypes.ImageURLPlaceholderDropdown]: hasSubscriptionPlaceholder(
                selectedImageURLPlaceholder?.Id,
            ),
        });
        // TODO: use 'validator' as dependency when it will be refactored to useCallback
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedImageURLPlaceholder, templateLangKey, hasSubscriptionPlaceholder]);

    return {
        handleImageURLPlaceholderDropdownChange,
        imagePreviewURL,
        imageURLPlaceholderDropdownData,
        isBlockedImageURLPlaceholderDropdown,
        isDisabledUploadFileBtn,
        isDisabledImageURLPlaceholderDropdown,
    };
};

export default useImageURLPlaceholder;
